import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Icons,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { OutlookThread, OutlookThreadMessage } from './OutlookInbox';

type OutlookThreadDialogProps = {
  open: boolean;
  thread?: OutlookThread;
  onClose: () => void;
};

type AttachmentProps = {
  attachment: NonNullable<OutlookThreadMessage['attachments']>[number];
};

const Attachment = ({ attachment }: AttachmentProps) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap={2}
    height="60px"
    p="8px 16px"
    sx={{
      border: '1px solid',
      borderColor: (theme) =>
        theme.palette.mode === 'dark' ? '#3A3247' : 'rgba(35, 31, 41, 0.16)',
      borderRadius: '4px',
      bgcolor: (theme) =>
        theme.palette.mode === 'dark' ? '#201A29' : '#F8F7F9',
    }}
  >
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={40}
      height={40}
      flexShrink={0}
      sx={{
        border: '1px solid',
        borderColor: (theme) =>
          theme.palette.mode === 'dark' ? '#413A50' : '#E2E8F0',
        borderRadius: '8px',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? '#1B1622' : '#FFFFFF',
      }}
    >
      <PictureAsPdfOutlinedIcon sx={{ color: '#F44343', fontSize: 24 }} />
    </Box>
    <Box minWidth={0} flex={1}>
      <Typography
        variant="subtitle2"
        fontWeight={500}
        noWrap
        sx={{
          fontSize: '14px',
          lineHeight: '21px',
          letterSpacing: '0.1px',
          color: (theme) =>
            theme.palette.mode === 'dark' ? '#FFFFFF' : '#0F172A',
        }}
      >
        {attachment.name}
      </Typography>
      {attachment.size ? (
        <Typography variant="body2" color="text.secondary" mt={0.25}>
          {attachment.size}
        </Typography>
      ) : null}
    </Box>
    <IconButton
      aria-label={`Download ${attachment.name}`}
      sx={{
        width: 40,
        height: 40,
        bgcolor: '#B0BEC5',
        color: 'common.white',
        '&:hover': { bgcolor: '#90A4AE' },
      }}
    >
      <FileDownloadIcon />
    </IconButton>
  </Box>
);

const OutlookThreadDialog = ({
  open,
  thread,
  onClose,
}: OutlookThreadDialogProps) => {
  const theme = useTheme();
  const messages = thread?.messages ?? [];
  const orderedMessages = [...messages].reverse();
  const isXsScreen = useMediaQuery('(max-width:599.95px)');
  const [expandedMessageIds, setExpandedMessageIds] = useState<string[]>([]);
  const hasHighImportance = orderedMessages.some(
    (message) => message.isImportant
  );
  useEffect(() => {
    if (!open) {
      setExpandedMessageIds([]);
    } else if (orderedMessages.length > 0) {
      setExpandedMessageIds([orderedMessages[0].id]);
    }
  }, [open]);

  const toggleMessage = (messageId: string) => {
    setExpandedMessageIds((currentIds) =>
      currentIds.includes(messageId)
        ? currentIds.filter((id) => id !== messageId)
        : [...currentIds, messageId]
    );
  };

  const handleReplyInOutlook = (message: OutlookThreadMessage) => {
    const recipientEmail =
      message.senderEmail ??
      messages.find((threadMessage) => threadMessage.senderEmail)?.senderEmail;

    const query = new URLSearchParams({
      subject: `RE: ${thread?.subject ?? ''}`,
    });

    if (recipientEmail) {
      query.set('to', recipientEmail);
    }

    window.open(
      `https://outlook.office.com/mail/deeplink/compose?${query.toString()}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isXsScreen}
      aria-labelledby="outlook-thread-title"
      PaperProps={{
        sx: {
          width: '974px',
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 32px)',
          borderRadius: { xs: 0, sm: '8px' },
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogContent
        sx={{
          pt: { xs: 2, sm: 3 },
          pr: { xs: 1.5, sm: 2 },
          pb: { xs: 2, sm: 3 },
          pl: { xs: 1.5, sm: 2 },
          gap: '10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          maxHeight: { xs: '100vh', sm: '80vh' },
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
        }}
      >
        {isXsScreen ? (
          <Box
            component="button"
            display="flex"
            alignItems="center"
            gap={1}
            onClick={onClose}
            sx={{
              border: 0,
              bgcolor: 'transparent',
              color: 'primary.main',
              cursor: 'pointer',
              p: 0,
              mb: 2,
            }}
          >
            <ArrowBackIcon />
            <Typography variant="subtitle1" fontWeight="medium">
              Back
            </Typography>
          </Box>
        ) : (
          <IconButton
            aria-label="Close email thread"
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: 'text.secondary',
            }}
          >
            <Icons.Close />
          </IconButton>
        )}

        <Box
          display="flex"
          flexDirection="column"
          minHeight={{ xs: 0, sm: '610px' }}
          flex={1}
          gap={3}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{
              mt: 4,
              pr: { xs: 5, sm: 0 },
              flexWrap: 'wrap',
            }}
          >
            <Typography
              id="outlook-thread-title"
              component="h2"
              variant="h5"
              fontWeight="bold"
              color="textPrimary"
            >
              {thread?.subject}
            </Typography>
            {hasHighImportance ? (
              <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                <PriorityHighIcon sx={{ color: '#D32F2F', fontSize: 18 }} />
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: '#D32F2F' }}
                >
                  High Importance
                </Typography>
              </Box>
            ) : null}
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {orderedMessages.map((message) => {
              const isExpanded = expandedMessageIds.includes(message.id);

              return (
                <Box
                  key={message.id}
                  sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    border: '1px solid',
                    borderColor: (theme) =>
                      theme.palette.mode === 'dark' ? '#3A3247' : 'divider',
                    borderRadius: 2,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '#201A29'
                        : 'background.paper',
                    p: { xs: 1.5, sm: 3 },
                    '& + &': {
                      mt: 1,
                    },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    gap={2}
                    width="100%"
                    minWidth={0}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleMessage(message.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleMessage(message.id);
                      }
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      gap={2}
                      flex={1}
                      minWidth={0}
                    >
                      <Avatar
                        sx={{
                          bgcolor: message.senderColor,
                          width: 48,
                          height: 48,
                          fontSize: 20,
                          flexShrink: 0,
                        }}
                      >
                        {message.senderInitials}
                      </Avatar>
                      <Box minWidth={0} flex={1}>
                        <Box
                          display="flex"
                          alignItems="baseline"
                          gap={1}
                          minWidth={0}
                          flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="textPrimary"
                            noWrap
                            sx={{ flexShrink: 0 }}
                          >
                            {message.senderName}
                          </Typography>
                          {message.senderEmail ? (
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={{ overflowWrap: 'anywhere' }}
                            >
                              &lt;{message.senderEmail}&gt;
                            </Typography>
                          ) : null}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={0.5}
                        >
                          {message.recipients}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexShrink={0}
                    >
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {message.sentLabel?.replace(/^Today\s*/i, '')}
                      </Typography>
                    </Box>
                  </Box>
                  {!isExpanded ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mt={1}
                      ml={8}
                      noWrap
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {message.body}
                    </Typography>
                  ) : null}

                  {isExpanded ? (
                    <>
                      {message.attachments?.length ? (
                        <Box mt={3}>
                          <Typography
                            variant="overline"
                            sx={{
                              fontSize: '14px',
                              letterSpacing: '1px',
                              color:
                                theme.palette.mode === 'dark'
                                  ? '#FFFFFF'
                                  : undefined,
                            }}
                          >
                            {message.attachments.length} ATTACHMENT
                            {message.attachments.length > 1 ? 'S' : ''}
                          </Typography>
                          <Box
                            display="flex"
                            flexDirection="column"
                            gap={1.5}
                            mt={2}
                          >
                            {message.attachments.map((attachment) => (
                              <Attachment
                                key={attachment.id}
                                attachment={attachment}
                              />
                            ))}
                          </Box>
                        </Box>
                      ) : null}

                      <Typography
                        variant="body1"
                        color="text.secondary"
                        mt={3}
                        sx={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}
                      >
                        {message.body}
                      </Typography>

                      <Box display="flex" justifyContent="flex-end" mt={3}>
                        <Button
                          kind="primary"
                          onClick={() => handleReplyInOutlook(message)}
                          startIcon={<OpenInNewIcon />}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Reply in Outlook
                        </Button>
                      </Box>
                    </>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OutlookThreadDialog;
